// Design tokens and shared primitives for the hiring department screens.
//
// WHY THIS FILE EXISTS
// These screens were first built in blue while the rest of the ATS chrome is
// teal, so they read as a bolted-on module rather than part of the product.
// Everything visual now comes from here, which also means the tree, the panel,
// the dialogs and the card grid cannot drift apart as they get edited.
//
// The palette is the module's own: #0c8a9b is what the sidebar highlights with
// and what JobCreationModal already uses, and the neutrals are the slate ramp
// the rest of the ATS is built from.

import { useMemo } from "react";
import {
  Box,
  Button,
  createTheme,
  ThemeProvider,
  Typography,
  useTheme,
} from "@mui/material";

export const T = {
  // One accent, used for the primary path through the screen and nothing else.
  accent: "#0c8a9b",
  accentDark: "#0a7480",
  accentDeep: "#075c68",
  accentWash: "#e8f6f7",
  accentLine: "#c2e6ea",

  // Semantic, kept separate from the accent so "done" never competes with
  // "this is the button you want".
  ok: "#15803d",
  okWash: "#f0fdf4",
  warn: "#b45309",
  warnWash: "#fffbeb",
  warnLine: "#fde68a",
  danger: "#dc2626",
  dangerWash: "#fef2f2",

  // Slate ramp.
  ink: "#0f172a",
  body: "#334155",
  muted: "#64748b",
  faint: "#94a3b8",
  line: "#e2e8f0",
  lineSoft: "#f1f5f9",
  surface: "#ffffff",
  canvas: "#f8fafc",

  font: "'Plus Jakarta Sans', 'Inter', sans-serif",
  radius: { sm: "8px", md: "10px", lg: "14px", pill: "999px" },
};

/**
 * Scopes the module accent to these screens.
 *
 * ATSLayout wraps the whole module in `premiumTheme`, whose palette.primary is
 * #D4AF37 — gold. So every MUI `contained` button, tab indicator, switch and
 * radio in here rendered gold, and an sx `bgcolor` loses that specificity fight.
 *
 * Overriding the palette for this subtree fixes all of them at once, which is
 * why this is a nested ThemeProvider rather than an `&&` override on each
 * component. The global theme is left alone deliberately: changing it would
 * restyle every button in the ATS, and that is a separate decision.
 */
export const DeptTheme = ({ children }) => {
  const outer = useTheme();
  const theme = useMemo(
    () =>
      createTheme(outer, {
        palette: {
          primary: {
            main: T.accent,
            dark: T.accentDark,
            light: T.accentWash,
            contrastText: "#ffffff",
          },
        },
      }),
    [outer],
  );
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

// Injected once. Only animations live here — everything else is sx, so there is
// no second styling system to keep in step.
if (
  typeof document !== "undefined" &&
  !document.getElementById("hb-dept-ui-keyframes")
) {
  const style = document.createElement("style");
  style.id = "hb-dept-ui-keyframes";
  style.textContent = `
    @keyframes hbDeptRise {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: none; }
    }
    @keyframes hbDeptPulse {
      0%, 100% { opacity: 1; }
      50% { opacity: .45; }
    }
  `;
  document.head.appendChild(style);
}

/** Small uppercase label that titles a group without competing with it. */
export const SectionLabel = ({ children, sx }) => (
  <Typography
    sx={{
      fontFamily: T.font,
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: "0.09em",
      textTransform: "uppercase",
      color: T.faint,
      ...sx,
    }}
  >
    {children}
  </Typography>
);

/** One sentence of plain-language help. Used wherever a term needs explaining. */
export const Hint = ({ children, sx }) => (
  <Typography
    sx={{
      fontFamily: T.font,
      fontSize: 13,
      lineHeight: 1.5,
      color: T.muted,
      ...sx,
    }}
  >
    {children}
  </Typography>
);

export const PrimaryButton = ({ children, sx, ...rest }) => (
  <Button
    variant="contained"
    disableElevation
    {...rest}
    sx={{
      fontFamily: T.font,
      textTransform: "none",
      fontWeight: 700,
      fontSize: 14,
      borderRadius: T.radius.md,
      px: 2,
      bgcolor: T.accent,
      "&:hover": { bgcolor: T.accentDark },
      "&.Mui-disabled": { bgcolor: T.line, color: T.faint },
      ...sx,
    }}
  >
    {children}
  </Button>
);

export const QuietButton = ({ children, sx, ...rest }) => (
  <Button
    variant="outlined"
    {...rest}
    sx={{
      fontFamily: T.font,
      textTransform: "none",
      fontWeight: 600,
      fontSize: 14,
      borderRadius: T.radius.md,
      color: T.body,
      borderColor: T.line,
      bgcolor: T.surface,
      "&:hover": { borderColor: T.accentLine, bgcolor: T.accentWash },
      ...sx,
    }}
  >
    {children}
  </Button>
);

/**
 * A count with its name attached. The first version of these screens showed
 * four bare icons in a row, which meant hovering each one to find out what it
 * counted — the label is the whole point.
 */
export const StatPill = ({ label, value, tone = "neutral" }) => {
  const tones = {
    neutral: { fg: T.body, bg: T.canvas, line: T.line },
    accent: { fg: T.accentDeep, bg: T.accentWash, line: T.accentLine },
    ok: { fg: T.ok, bg: T.okWash, line: "#bbf7d0" },
    warn: { fg: T.warn, bg: T.warnWash, line: T.warnLine },
  };
  const c = tones[tone] || tones.neutral;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        gap: 0.6,
        px: 1,
        py: 0.4,
        borderRadius: T.radius.sm,
        bgcolor: c.bg,
        border: `1px solid ${c.line}`,
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontFamily: T.font,
          fontWeight: 800,
          fontSize: 13.5,
          color: c.fg,
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          fontFamily: T.font,
          fontSize: 11.5,
          fontWeight: 600,
          color: c.fg,
          opacity: 0.75,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

/**
 * Whether a team or role can actually be hired into yet.
 *
 * A level with no job template cannot open a job, and that was previously only
 * discoverable by clicking into its Templates tab and finding it empty. Encoding
 * it as state means the tree answers "what still needs work" at a glance.
 */
export const readinessOf = (node) => {
  const templates = node?.template_count ?? 0;
  const members = node?.member_count ?? 0;
  if (templates > 0 && members > 0)
    return { key: "ready", label: "Ready to hire", tone: "ok" };
  if (templates > 0)
    return { key: "no-people", label: "No people yet", tone: "warn" };
  return { key: "no-template", label: "Needs a job template", tone: "warn" };
};

export const ReadinessDot = ({ node, withLabel = false }) => {
  const state = readinessOf(node);
  const color = state.tone === "ok" ? T.ok : T.warn;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, minWidth: 0 }}>
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          bgcolor: color,
          flex: "none",
        }}
      />
      {withLabel && (
        <Typography
          sx={{
            fontFamily: T.font,
            fontSize: 12,
            fontWeight: 600,
            color,
            whiteSpace: "nowrap",
          }}
        >
          {state.label}
        </Typography>
      )}
    </Box>
  );
};

/**
 * The one thing to do next, with the button that does it.
 *
 * This is the main fix for "the screen is confusing": rather than expecting
 * someone to infer the department → team → job template → job chain from a
 * layout, the screen says which step it is on and offers that step.
 */
export const NextStep = ({ title, detail, actionLabel, onAction, icon }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      flexWrap: "wrap",
      bgcolor: T.accentWash,
      border: `1px solid ${T.accentLine}`,
      borderRadius: T.radius.lg,
      px: 2,
      py: 1.5,
      animation: "hbDeptRise .25s ease both",
    }}
  >
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: T.radius.md,
        bgcolor: T.surface,
        color: T.accent,
        display: "grid",
        placeItems: "center",
        flex: "none",
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 180 }}>
      <Typography
        sx={{
          fontFamily: T.font,
          fontWeight: 700,
          fontSize: 14,
          color: T.accentDeep,
        }}
      >
        {title}
      </Typography>
      {detail && (
        <Typography
          sx={{ fontFamily: T.font, fontSize: 12.5, color: T.muted, mt: 0.1 }}
        >
          {detail}
        </Typography>
      )}
    </Box>
    {actionLabel && onAction && (
      <PrimaryButton size="small" onClick={onAction}>
        {actionLabel}
      </PrimaryButton>
    )}
  </Box>
);

/**
 * The four stages of setting a department up, with live counts.
 *
 * Shown as a progress row rather than a legend: the point is not to define the
 * words, it is to show which stage this department has actually reached.
 */
export const PipelineBar = ({ steps }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "stretch",
      gap: 1,
      flexWrap: "wrap",
    }}
  >
    {steps.map((step, idx) => {
      const done = step.value > 0;
      // The first incomplete stage is the one to work on, and only that one is
      // highlighted — two "current" markers would be no marker at all.
      const isCurrent = !done && steps.slice(0, idx).every((s) => s.value > 0);
      return (
        <Box
          key={step.label}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.4,
            py: 1,
            flex: "1 1 150px",
            minWidth: 0,
            borderRadius: T.radius.md,
            bgcolor: isCurrent ? T.accentWash : T.surface,
            border: `1px solid ${isCurrent ? T.accentLine : T.line}`,
          }}
        >
          {/* No step number: it sat directly beside a count ("3" next to "0
              written") and read as part of it. Done, current, and not-yet are
              distinguishable without one. */}
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              flex: "none",
              display: "grid",
              placeItems: "center",
              fontFamily: T.font,
              fontSize: 12,
              fontWeight: 800,
              bgcolor: done ? T.ok : isCurrent ? T.accent : "transparent",
              border: done || isCurrent ? "none" : `2px solid ${T.line}`,
              color: "#fff",
              animation: isCurrent ? "hbDeptPulse 2.4s ease infinite" : "none",
            }}
          >
            {done ? "✓" : ""}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: T.font,
                fontSize: 12.5,
                fontWeight: 700,
                color: done || isCurrent ? T.ink : T.muted,
                lineHeight: 1.25,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {step.label}
            </Typography>
            <Typography
              sx={{
                fontFamily: T.font,
                fontSize: 11.5,
                color: T.faint,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {step.value} {step.unit}
            </Typography>
          </Box>
        </Box>
      );
    })}
  </Box>
);

/** Guided empty state: says what this is for, then offers the one next action. */
export const EmptyState = ({ icon, title, body, action, secondary }) => (
  <Box
    sx={{
      textAlign: "center",
      px: 3,
      py: 5,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 1,
    }}
  >
    <Box
      sx={{
        width: 46,
        height: 46,
        borderRadius: T.radius.lg,
        bgcolor: T.accentWash,
        color: T.accent,
        display: "grid",
        placeItems: "center",
        mb: 0.5,
      }}
    >
      {icon}
    </Box>
    <Typography
      sx={{ fontFamily: T.font, fontWeight: 800, fontSize: 15.5, color: T.ink }}
    >
      {title}
    </Typography>
    {body && <Hint sx={{ maxWidth: 380 }}>{body}</Hint>}
    {(action || secondary) && (
      <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap", justifyContent: "center" }}>
        {action}
        {secondary}
      </Box>
    )}
  </Box>
);

export const initialsOf = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0] || "")
    .join("")
    .toUpperCase() || "?";

// Plain-language names for the structural types. The stored values stay
// DIVISION/TEAM/ROLE/GRADE; only what people read changes.
export const TYPE_WORDS = {
  dept: { label: "Department", hint: "The top of one hiring structure" },
  div: { label: "Division", hint: "A large part of the department" },
  team: { label: "Team", hint: "A group that works together" },
  role: { label: "Role", hint: "A job people are actually hired into" },
  grade: { label: "Grade", hint: "A seniority band within a role" },
};

export const ROLE_WORDS = {
  LEAD: { label: "Lead", hint: "Runs this team or role" },
  HIRING_MANAGER: { label: "Hiring manager", hint: "Owns the hiring decision" },
  RECRUITER: { label: "Recruiter", hint: "Runs the process day to day" },
  INTERVIEWER: { label: "Interviewer", hint: "Takes interview rounds" },
  MEMBER: { label: "Member", hint: "Works here, no hiring duty" },
};
